import { ResponsiveContainer, BarChart, Bar, ComposedChart, Line, Area, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, LabelList } from "recharts";

/** Label nilai ton di atas batang / titik garis (0 disembunyikan) */
const fmtVal = (v) => (v == null || Number(v) === 0 ? "" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 1 }));
const VAL_LABEL = { position: "top", fill: "#CBD5E1", fontSize: 10, fontFamily: "JetBrains Mono, monospace", formatter: fmtVal };
import { Download } from "lucide-react";

export const CHART_COLORS = { target: "#0EA5E9", qc: "#10B981", stb: "#6366F1", delivery: "#F59E0B" };
export const AREA_COLORS = ["#0EA5E9", "#10B981", "#6366F1", "#F59E0B", "#22D3EE", "#F472B6"];

const axis = { stroke: "#334155", tick: { fill: "#64748B", fontSize: 11 } };

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded border border-slate-700 bg-[#0D1524] px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-medium text-slate-300">{label}{payload[0]?.payload?.date ? <span className="ml-1.5 font-mono text-[10px] text-slate-500">{payload[0].payload.date}</span> : null}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-slate-300">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-mono text-slate-100">{Number(p.value).toLocaleString("id-ID")}</span>
        </div>
      ))}
    </div>
  );
};

const legendStyle = { color: "#94A3B8", fontSize: 11 };

const VendorTick = ({ x, y, payload, data }) => {
  const row = data.find((d) => d.label === payload.value);
  const below = row?.belowTarget;
  return (
    <text x={x} y={y} dy={4} transform={`rotate(-24 ${x} ${y})`} textAnchor="end" fontSize={11} fill={below ? "#FB7185" : "#64748B"} fontWeight={below ? 700 : 400}>
      {below ? "▼ " : ""}{payload.value}
    </text>
  );
};

export function VendorBarChart({ data, onBarClick, height = 270 }) {
  const hasTarget = data.some((d) => d.targetStb > 0);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 18, right: 8, left: -18, bottom: 0 }} style={onBarClick ? { cursor: "pointer" } : undefined}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
        <XAxis dataKey="label" tick={<VendorTick data={data} />} axisLine={{ stroke: "#334155" }} tickLine={false} interval={0} height={54} />
        <YAxis tick={axis.tick} axisLine={false} tickLine={false} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(30,41,59,.4)" }} />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="stb" name="STB (Ton)" fill={CHART_COLORS.stb} radius={[2, 2, 0, 0]} maxBarSize={28} onClick={(d) => onBarClick?.(d?.payload || d)} className={onBarClick ? "cursor-pointer" : ""}>
          {data.map((d, i) => <Cell key={i} fill={d.belowTarget ? "#F43F5E" : CHART_COLORS.stb} />)}
          <LabelList dataKey="stb" {...VAL_LABEL} />
        </Bar>
        {hasTarget && <Bar dataKey="targetStb" name="Target STB (Ton)" fill="rgba(245,158,11,0.08)" stroke="#F59E0B" strokeDasharray="4 3" radius={[2, 2, 0, 0]} maxBarSize={28} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const GRAN_LABEL = { month: "Bulanan", week: "Mingguan", day: "Harian" };

export function TrendGranularityToggle({ value, onChange, testid = "trend-granularity" }) {
  return (
    <span className="inline-flex overflow-hidden rounded border border-slate-800" data-testid={testid}>
      {Object.entries(GRAN_LABEL).map(([k, t]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`px-2 py-0.5 font-mono text-[10px] transition-colors ${value === k ? "bg-sky-500/20 text-sky-300" : "text-slate-500 hover:text-slate-300"}`} data-testid={`${testid}-${k}`}>{t}</button>
      ))}
    </span>
  );
}

export function TrendChart({ data, granularity = "month", height = 270 }) {
  const g = GRAN_LABEL[granularity] || "Bulanan";
  const many = data.length > 16;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: many ? 36 : 18, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
        <XAxis dataKey="month" tick={axis.tick} axisLine={{ stroke: "#334155" }} tickLine={false} interval={many ? "preserveStartEnd" : 0} minTickGap={many ? 18 : 0} />
        <YAxis tick={axis.tick} axisLine={false} tickLine={false} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(30,41,59,.4)" }} />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="stb" name={`STB ${g} (Ton)`} fill={CHART_COLORS.stb} radius={[2, 2, 0, 0]} maxBarSize={22}>
          {data.map((d, i) => <Cell key={i} fill={d.target && d.stb < d.target ? "#F43F5E" : CHART_COLORS.stb} />)}
          {/* nilai tonase selalu tampil — harian (31 batang) memakai font lebih kecil & miring agar tidak bertumpuk */}
          <LabelList dataKey="stb" {...VAL_LABEL} fontSize={many ? 8 : 10} angle={many ? -60 : 0} offset={many ? 14 : 5} />
        </Bar>
        <Line type="monotone" dataKey="target" name={`Target ${g} (Ton)`} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 4" dot={false} />
        <Line type="monotone" dataKey="kumulatif" name="STB Kumulatif (Ton)" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }}>
          {!many && <LabelList dataKey="kumulatif" {...VAL_LABEL} fill="#67E8F9" offset={10} />}
        </Line>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function AreaBarChart({ data, height = 270 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 18, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
        <XAxis dataKey="area" tick={axis.tick} axisLine={{ stroke: "#334155" }} tickLine={false} />
        <YAxis tick={axis.tick} axisLine={false} tickLine={false} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(30,41,59,.4)" }} />
        <Legend wrapperStyle={legendStyle} />
        <Bar dataKey="value" name="STB (Ton)" fill={CHART_COLORS.stb} radius={[2, 2, 0, 0]} maxBarSize={34}>
          {data.map((_, i) => <Cell key={i} fill={AREA_COLORS[i % AREA_COLORS.length]} />)}
          <LabelList dataKey="value" {...VAL_LABEL} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

const mainSvg = (container) => container?.querySelector(".recharts-wrapper > svg.recharts-surface");

const svgToImage = (svg) => new Promise((resolve) => {
  const rect = svg.getBoundingClientRect();
  const w = Math.round(rect.width) || Number(svg.getAttribute("width")) || 600;
  const h = Math.round(rect.height) || Number(svg.getAttribute("height")) || 270;
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", w); clone.setAttribute("height", h);
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml;charset=utf-8" }));
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve({ img, w, h }); };
  img.src = url;
});

export async function downloadChartsCombined(items, { filename, heading, subheading }) {
  const loaded = [];
  for (const it of items) {
    const svg = mainSvg(it.container);
    if (svg) loaded.push({ ...(await svgToImage(svg)), title: it.title });
  }
  if (!loaded.length) return false;
  const scale = 2, pad = 28, gap = 24, head = 86, titleH = 28;
  const totalW = loaded.reduce((a, l) => a + l.w, 0) + gap * (loaded.length - 1) + pad * 2;
  const maxH = Math.max(...loaded.map((l) => l.h));
  const totalH = pad + head + titleH + maxH + pad + 22;
  const c = document.createElement("canvas");
  c.width = totalW * scale; c.height = totalH * scale;
  const ctx = c.getContext("2d");
  ctx.scale(scale, scale);
  ctx.fillStyle = "#0F141C"; ctx.fillRect(0, 0, totalW, totalH);
  ctx.fillStyle = "#0EA5E9"; ctx.fillRect(pad, pad, 6, 44);
  ctx.fillStyle = "#F1F5F9"; ctx.font = "900 24px sans-serif"; ctx.fillText(heading, pad + 18, pad + 22);
  ctx.fillStyle = "#94A3B8"; ctx.font = "13px sans-serif"; ctx.fillText(subheading, pad + 18, pad + 44);
  let x = pad;
  for (const l of loaded) {
    ctx.fillStyle = "#1E293B"; ctx.fillRect(x, pad + head - 10, l.w, 1);
    ctx.fillStyle = "#E2E8F0"; ctx.font = "bold 14px sans-serif"; ctx.fillText(l.title, x, pad + head + 12);
    ctx.drawImage(l.img, x, pad + head + titleH, l.w, l.h);
    x += l.w + gap;
  }
  ctx.fillStyle = "#475569"; ctx.font = "11px sans-serif";
  ctx.fillText(`HONGLU FABRIKASI SISTEM · ${new Date().toLocaleString("id-ID")}`, pad, totalH - 14);
  const a = document.createElement("a");
  a.href = c.toDataURL("image/png"); a.download = `${filename}.png`; a.click();
  return true;
}

export async function downloadChartPng(container, filename, title) {
  const svg = mainSvg(container);
  if (!svg) return false;
  const { img, w, h } = await svgToImage(svg);
  const scale = 2, pad = 24, head = title ? 40 : 0;
  const c = document.createElement("canvas");
  c.width = (w + pad * 2) * scale; c.height = (h + pad * 2 + head) * scale;
  const ctx = c.getContext("2d");
  ctx.scale(scale, scale);
  ctx.fillStyle = "#0F141C"; ctx.fillRect(0, 0, w + pad * 2, h + pad * 2 + head);
  if (title) { ctx.fillStyle = "#E2E8F0"; ctx.font = "bold 15px sans-serif"; ctx.fillText(title, pad, pad + 14); }
  ctx.drawImage(img, pad, pad + head, w, h);
  const a = document.createElement("a");
  a.href = c.toDataURL("image/png"); a.download = `${filename}.png`; a.click();
  return true;
}

export function ChartExportButton({ targetRef, filename, title, testid }) {
  return (
    <button type="button" onClick={() => downloadChartPng(targetRef.current, filename, title)}
      className="inline-flex h-6 w-6 items-center justify-center rounded border border-slate-800 text-slate-500 transition-colors hover:border-sky-500/50 hover:text-sky-300"
      title="Unduh PNG / 下载图片" data-testid={testid}>
      <Download className="h-3.5 w-3.5" />
    </button>
  );
}

export function SCurveChart({ data, todayLabel }) {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="scurvePlan" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.25} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
        <XAxis dataKey="label" tick={axis.tick} axisLine={{ stroke: "#334155" }} tickLine={false} interval="preserveStartEnd" minTickGap={28} />
        <YAxis tick={axis.tick} axisLine={false} tickLine={false} />
        <Tooltip content={<Tip />} />
        <Legend wrapperStyle={legendStyle} />
        <Area type="monotone" dataKey="plan" name="Rencana Kumulatif (Ton)" stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 4" fill="url(#scurvePlan)" dot={false} />
        <Line type="monotone" dataKey="actual" name="Realisasi STB (Ton)" stroke="#22D3EE" strokeWidth={2.5} dot={{ r: 2 }} connectNulls={false} />
        {todayLabel && <ReferenceLine x={todayLabel} stroke="#94A3B8" strokeDasharray="2 4" label={{ value: "Hari ini", fill: "#94A3B8", fontSize: 10, position: "insideTopRight" }} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
